import * as React from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './core/auth/AuthProvider';
import { BrandingProvider } from './core/branding/BrandingProvider';
import { RequireAuth } from './core/auth/RequireAuth';
import { LoginPage } from './core/auth/LoginPage';
import { AdminLoginPage } from './core/auth/AdminLoginPage';
import { SetPasswordPage } from './core/auth/SetPasswordPage';
import { ForgotPasswordPage } from './core/auth/ForgotPasswordPage';
import { SuperAdminShell } from './super-admin/SuperAdminShell';
import { TenantsListPage } from './super-admin/TenantsListPage';
import { CreateTenantPage } from './super-admin/CreateTenantPage';
import { DashboardShell } from './dashboard/DashboardShell';
import { HomePage } from './dashboard/HomePage';
import { ReportsPage as ReportsCenterPage } from './modules/reports/pages/ReportsPage';
import { Toaster } from './components/ui/sonner';
import { ConfirmProvider } from './components/ui/confirm-dialog';

// Invoicing module pages
import { InvoicesPage } from './modules/invoicing/pages/InvoicesPage';
import { InvoiceEditorPage } from './modules/invoicing/pages/InvoiceEditorPage';
import { InvoiceDetailPage } from './modules/invoicing/pages/InvoiceDetailPage';
import { RecurringEditorPage } from './modules/invoicing/pages/RecurringEditorPage';
import { ClientsPage } from './modules/invoicing/pages/ClientsPage';
import { ClientDetailPage } from './modules/invoicing/pages/ClientDetailPage';
import { ClientEditorPage } from './modules/invoicing/pages/ClientEditorPage';
import { ServicesPage } from './modules/invoicing/pages/ServicesPage';
import { ServiceEditorPage } from './modules/invoicing/pages/ServiceEditorPage';
import { SettingsPage as InvoicingSettingsPage } from './modules/invoicing/pages/SettingsPage';
import { SettingsPage } from './dashboard/SettingsPage';
import { EmailSettingsPage } from './modules/email/pages/EmailSettingsPage';
import { PaymentsPage } from './modules/payments/pages/PaymentsPage';
import { TeamPage } from './modules/hr/pages/TeamPage';
import { EmployeeDetailPage } from './modules/hr/pages/EmployeeDetailPage';
import { OrgChartPage } from './modules/hr/pages/OrgChartPage';
import { AttendancePage } from './modules/hr/pages/AttendancePage';
import { LeavePage } from './modules/hr/pages/LeavePage';
import { PenaltyRulesPage } from './modules/hr/pages/PenaltyRulesPage';
import { SchedulePage } from './modules/hr/pages/SchedulePage';
import { MySchedulePage } from './modules/hr/pages/MySchedulePage';
import { PerformancePage } from './modules/hr/pages/PerformancePage';
import { MyPerformancePage } from './modules/hr/pages/MyPerformancePage';
import { PayrollPage } from './modules/payroll/pages/PayrollPage';
import { RunDetailPage } from './modules/payroll/pages/RunDetailPage';
import { PayslipsListPage } from './modules/payroll/pages/PayslipsListPage';
import { PayslipDetailPage } from './modules/payroll/pages/PayslipDetailPage';
import { PayrollReportsPage } from './modules/payroll/pages/PayrollReportsPage';
import { MonitoringPage } from './modules/monitoring/pages/MonitoringPage';
import { MonitoringSettingsPage } from './modules/monitoring/pages/MonitoringSettingsPage';
import { MonitoringAnalyticsPage } from './modules/monitoring/pages/MonitoringAnalyticsPage';
import { AgentConnectPage } from './modules/monitoring/pages/AgentConnectPage';
import { ProjectsPage } from './modules/projects/pages/ProjectsPage';
import { ProjectDetailPage } from './modules/projects/pages/ProjectDetailPage';
import { ReportsPage } from './modules/projects/pages/ReportsPage';
import { MyTasksPage } from './modules/projects/pages/MyTasksPage';
import { TimesheetApprovalsPage } from './modules/projects/pages/TimesheetApprovalsPage';
import { ExpensesPage } from './modules/expenses/pages/ExpensesPage';
import { ExpenseDetailPage } from './modules/expenses/pages/ExpenseDetailPage';
import { ExpenseEditorPage } from './modules/expenses/pages/ExpenseEditorPage';
import { CategoriesPage as ExpenseCategoriesPage } from './modules/expenses/pages/CategoriesPage';
import { PendingApprovalsPage as ExpensePendingApprovalsPage } from './modules/expenses/pages/PendingApprovalsPage';
import { ReimbursePage as ExpenseReimbursePage } from './modules/expenses/pages/ReimbursePage';
import { ApprovalChainsPage as ExpenseApprovalChainsPage } from './modules/expenses/pages/ApprovalChainsPage';
import { ApprovalPoliciesPage as ExpenseApprovalPoliciesPage } from './modules/expenses/pages/ApprovalPoliciesPage';
import { DocumentsListPage } from './modules/esign/pages/DocumentsListPage';
import { PrepareWizardPage } from './modules/esign/pages/PrepareWizardPage';
import { DocumentDetailPage } from './modules/esign/pages/DocumentDetailPage';
import { TemplatesPage as EsignTemplatesPage } from './modules/esign/pages/TemplatesPage';
import { TemplateEditorPage as EsignTemplateEditorPage } from './modules/esign/pages/TemplateEditorPage';
import { SignPage } from './modules/esign/pages/SignPage';
import { TrialWizardPage } from './dashboard/TrialWizardPage';
import { ModulePricesPage } from './super-admin/billing/ModulePricesPage';
import { SubscriptionsListPage } from './super-admin/billing/SubscriptionsListPage';
import { BillingReportsPage } from './super-admin/billing/BillingReportsPage';
import { AgentReleasesPage } from './super-admin/AgentReleasesPage';
import { TenantDetailPage } from './super-admin/TenantDetailPage';
import { DashboardPage as SuperAdminDashboardPage } from './super-admin/DashboardPage';
import { CustomerReviewsPage } from './super-admin/site/CustomerReviewsPage';
import { BlogPostsPage } from './super-admin/site/BlogPostsPage';
import { BlogPostEditorPage } from './super-admin/site/BlogPostEditorPage';
import { HelpDocsPage } from './super-admin/site/HelpDocsPage';
import { HelpDocEditorPage } from './super-admin/site/HelpDocEditorPage';
import { SeoSettingsPage } from './super-admin/site/SeoSettingsPage';
import { AdminRolesPage } from './super-admin/access/AdminRolesPage';
import { AnnouncementsPage } from './super-admin/announcements/AnnouncementsPage';
import { PlatformAuditPage } from './super-admin/PlatformAuditPage';
import { RequireAdminPermission } from './core/auth/RequireAdminPermission';
import { RequireUserPermission } from './core/auth/RequireUserPermission';
import { BillingPage } from './modules/platform-billing/pages/BillingPage';
import { ErrorBoundary } from './core/errors/ErrorBoundary';
import { NotFoundPage } from './core/errors/NotFoundPage';
import { ThemeProvider } from './core/theme/ThemeProvider';
import { MarketingLayout } from './marketing/MarketingLayout';
import { LandingPage } from './marketing/pages/LandingPage';
import { PricingPage } from './marketing/pages/PricingPage';
import { SignupPage } from './marketing/pages/SignupPage';
import { StatusPage } from './marketing/pages/StatusPage';
import { CustomersPage } from './marketing/pages/CustomersPage';
import { CustomerDetailPage } from './marketing/pages/CustomerDetailPage';
import { FeaturesPage } from './marketing/pages/FeaturesPage';
import { SecurityPage } from './marketing/pages/SecurityPage';
import { IntegrationsPage } from './marketing/pages/IntegrationsPage';
import { ChangelogPage } from './marketing/pages/ChangelogPage';
import { AboutPage } from './marketing/pages/AboutPage';
import { BlogIndexPage } from './marketing/pages/BlogIndexPage';
import { BlogPostPage } from './marketing/pages/BlogPostPage';
import { DocsIndexPage } from './marketing/pages/DocsIndexPage';
import { DocsArticlePage } from './marketing/pages/DocsArticlePage';
import { ContactPage } from './marketing/pages/ContactPage';
import { VerifyPage } from './marketing/pages/VerifyPage';
import { PaySuccessPage, PayCancelPage } from './marketing/pages/PayReturnPage';
import { TermsPage } from './marketing/pages/legal/TermsPage';
import { PrivacyPage } from './marketing/pages/legal/PrivacyPage';
import { DpaPage } from './marketing/pages/legal/DpaPage';
import { AupPage } from './marketing/pages/legal/AupPage';
import { SupportPage } from './dashboard/SupportPage';
import { NotificationsPage } from './modules/notifications/pages/NotificationsPage';
import { AccessPage } from './modules/workspace/pages/AccessPage';
import { IntegrationsPage as QuickBooksIntegrationsPage } from './modules/integrations/pages/IntegrationsPage';
import { SupportInboxPage } from './super-admin/support/SupportInboxPage';
import { SupportTicketPage } from './super-admin/support/SupportTicketPage';
import { getHostMode, APP_HOST, MARKETING_HOST } from './core/hostMode';
import { EnabledModulesProvider } from './core/modules/EnabledModulesProvider';
import { RequireModule } from './core/modules/RequireModule';
import { TourProvider } from './modules/onboarding/TourProvider';
import { OnboardingProvider } from './modules/onboarding/OnboardingProvider';
import { OnboardingAutoAdvance } from './modules/onboarding/OnboardingAutoAdvance';

function App() {
  const mode = getHostMode();
  const showMarketing = mode === 'marketing' || mode === 'dev';
  const showApp       = mode === 'app'       || mode === 'dev';

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <BrandingProvider>
            <Toaster richColors closeButton position="bottom-right" />
            <AuthProvider>
              <ConfirmProvider>
              <Routes>
                {/* Auth pages are available on both hosts. */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                {/* Public, host-agnostic signer experience — no auth, no dashboard chrome. */}
                <Route path="/sign/:token" element={<SignPage />} />
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/auth/set-password" element={<SetPasswordPage />} />

                {/* ──────────── APP HOST (app.worqly.io) ──────────── */}
                {showApp && (
                  <Route path="/admin" element={<RequireAuth requireType="super_admin"><SuperAdminShell /></RequireAuth>}>
                    <Route index element={<RequireAdminPermission permission="tenants.read"><SuperAdminDashboardPage /></RequireAdminPermission>} />
                    <Route path="tenants" element={<RequireAdminPermission permission="tenants.read"><TenantsListPage /></RequireAdminPermission>} />
                    <Route path="tenants/new" element={<RequireAdminPermission permission="tenants.write"><CreateTenantPage /></RequireAdminPermission>} />
                    <Route path="tenants/:id" element={<RequireAdminPermission permission="tenants.read"><TenantDetailPage /></RequireAdminPermission>} />
                    <Route path="billing/prices"        element={<RequireAdminPermission permission="billing.read"><ModulePricesPage /></RequireAdminPermission>} />
                    <Route path="billing/subscriptions" element={<RequireAdminPermission permission="billing.read"><SubscriptionsListPage /></RequireAdminPermission>} />
                    <Route path="billing/reports"        element={<RequireAdminPermission permission="billing.read"><BillingReportsPage /></RequireAdminPermission>} />
                    <Route path="agent-releases" element={<RequireAdminPermission permission="releases.read"><AgentReleasesPage /></RequireAdminPermission>} />

                    {/* ----- Site content ----- */}
                    <Route path="site/reviews"      element={<RequireAdminPermission permission="site.reviews.read"><CustomerReviewsPage /></RequireAdminPermission>} />
                    <Route path="site/blog"         element={<RequireAdminPermission permission="site.blog.read"><BlogPostsPage /></RequireAdminPermission>} />
                    <Route path="site/blog/new"     element={<RequireAdminPermission permission="site.blog.write"><BlogPostEditorPage /></RequireAdminPermission>} />
                    <Route path="site/blog/:id"     element={<RequireAdminPermission permission="site.blog.read"><BlogPostEditorPage /></RequireAdminPermission>} />
                    <Route path="site/docs"         element={<RequireAdminPermission permission="site.docs.read"><HelpDocsPage /></RequireAdminPermission>} />
                    <Route path="site/docs/new"     element={<RequireAdminPermission permission="site.docs.write"><HelpDocEditorPage /></RequireAdminPermission>} />
                    <Route path="site/docs/:id"     element={<RequireAdminPermission permission="site.docs.read"><HelpDocEditorPage /></RequireAdminPermission>} />
                    <Route path="site/seo"          element={<RequireAdminPermission permission="site.seo.read"><SeoSettingsPage /></RequireAdminPermission>} />

                    {/* ----- Access / RBAC ----- */}
                    <Route path="access/admins"     element={<RequireAdminPermission permission="admins.read"><AdminRolesPage /></RequireAdminPermission>} />

                    {/* ----- Announcements (platform -> tenants) ----- */}
                    <Route path="announcements" element={<RequireAdminPermission permission="announcements.read"><AnnouncementsPage /></RequireAdminPermission>} />

                    {/* ----- Support ----- */}
                    <Route path="support"     element={<RequireAdminPermission permission="support.read"><SupportInboxPage /></RequireAdminPermission>} />
                    <Route path="support/:id" element={<RequireAdminPermission permission="support.read"><SupportTicketPage /></RequireAdminPermission>} />

                    {/* ----- Operations / Audit ----- */}
                    <Route path="audit"       element={<RequireAdminPermission permission="tenants.read"><PlatformAuditPage /></RequireAdminPermission>} />
                  </Route>
                )}

                {showApp && (
                  <Route path="/dashboard" element={<RequireAuth requireType="user"><EnabledModulesProvider><OnboardingProvider><TourProvider><OnboardingAutoAdvance /><DashboardShell /></TourProvider></OnboardingProvider></EnabledModulesProvider></RequireAuth>}>
                    {/* ── Unguarded routes (always accessible) ── */}
                    <Route index element={<HomePage />} />
                    <Route path="settings"             element={<SettingsPage />} />
                    <Route path="settings/access"      element={<AccessPage />} />
                    <Route path="settings/integrations" element={<QuickBooksIntegrationsPage />} />
                    <Route path="notifications"        element={<NotificationsPage />} />
                    <Route path="support"              element={<SupportPage />} />
                    <Route path="billing"              element={<BillingPage />} />
                    <Route path="billing/trial-wizard" element={<TrialWizardPage />} />
                    {/* One-visit agent setup handoff — handles its own
                        not-monitored / already-connected states, so it isn't
                        gated by RequireModule. Also reachable at the bare
                        /agent/connect URL (redirect below). */}
                    <Route path="agent/connect"        element={<AgentConnectPage />} />
                    {/* Reports is cross-module (workforce + financial) — gated
                        on reports.view inside ReportsPage itself, not a
                        RequireModule guard. */}
                    <Route path="reports"               element={<ReportsCenterPage />} />

                    {/* ── invoicing — guarded ── */}
                    <Route element={<RequireModule module="invoicing" />}>
                      <Route path="invoices"                       element={<RequireUserPermission permission="invoices.view"><InvoicesPage /></RequireUserPermission>} />
                      <Route path="invoices/recurring"             element={<RequireUserPermission permission="invoices.view"><InvoicesPage /></RequireUserPermission>} />
                      <Route path="invoices/new"                   element={<RequireUserPermission permission="invoices.view"><InvoiceEditorPage /></RequireUserPermission>} />
                      <Route path="invoices/:id"                   element={<RequireUserPermission permission="invoices.view"><InvoiceDetailPage /></RequireUserPermission>} />
                      <Route path="invoices/:id/edit"              element={<RequireUserPermission permission="invoices.view"><InvoiceEditorPage /></RequireUserPermission>} />
                      <Route path="invoices/recurring/new"         element={<RequireUserPermission permission="invoices.view"><RecurringEditorPage /></RequireUserPermission>} />
                      <Route path="invoices/recurring/:id/edit"    element={<RequireUserPermission permission="invoices.view"><RecurringEditorPage /></RequireUserPermission>} />
                      <Route path="clients"                        element={<RequireUserPermission permission="clients.view"><ClientsPage /></RequireUserPermission>} />
                      <Route path="clients/new"                    element={<RequireUserPermission permission="clients.view"><ClientEditorPage /></RequireUserPermission>} />
                      <Route path="clients/:id"                    element={<RequireUserPermission permission="clients.view"><ClientDetailPage /></RequireUserPermission>} />
                      <Route path="clients/:id/edit"               element={<RequireUserPermission permission="clients.view"><ClientEditorPage /></RequireUserPermission>} />
                      <Route path="services"                       element={<RequireUserPermission permission="services.view"><ServicesPage /></RequireUserPermission>} />
                      <Route path="services/new"                   element={<RequireUserPermission permission="services.view"><ServiceEditorPage /></RequireUserPermission>} />
                      <Route path="services/:id/edit"              element={<RequireUserPermission permission="services.view"><ServiceEditorPage /></RequireUserPermission>} />
                      {/* Nav gates this at invoices.write (DashboardShell.tsx) — mirror it. */}
                      <Route path="settings/invoicing"             element={<RequireUserPermission permission="invoices.write"><InvoicingSettingsPage /></RequireUserPermission>} />
                    </Route>

                    {/* ── email — guarded ── */}
                    <Route element={<RequireModule module="email" />}>
                      <Route path="email" element={<RequireUserPermission permission="email.view"><EmailSettingsPage /></RequireUserPermission>} />
                    </Route>

                    {/* ── payments — guarded ── */}
                    <Route element={<RequireModule module="payments" />}>
                      <Route path="payments" element={<RequireUserPermission permission="payments.manage"><PaymentsPage /></RequireUserPermission>} />
                    </Route>

                    {/* ── hr — guarded ── */}
                    <Route element={<RequireModule module="hr" />}>
                      <Route path="hr/team"          element={<TeamPage />} />
                      <Route path="hr/team/:id"      element={<EmployeeDetailPage />} />
                      <Route path="hr/org-chart"     element={<OrgChartPage />} />
                      <Route path="hr/attendance"    element={<AttendancePage />} />
                      <Route path="hr/leave"         element={<LeavePage />} />
                      <Route path="hr/penalty-rules" element={<PenaltyRulesPage />} />
                      <Route path="hr/schedule"      element={<SchedulePage />} />
                      <Route path="hr/my-schedule"   element={<MySchedulePage />} />
                      <Route path="hr/performance"    element={<PerformancePage />} />
                      <Route path="hr/my-performance" element={<MyPerformancePage />} />
                    </Route>

                    {/* ── payroll — guarded ── */}
                    <Route element={<RequireModule module="payroll" />}>
                      <Route path="payroll"                  element={<PayrollPage />} />
                      <Route path="payroll/runs/:id"         element={<RunDetailPage />} />
                      <Route path="payroll/payslips"         element={<PayslipsListPage />} />
                      <Route path="payroll/payslips/:id"     element={<PayslipDetailPage />} />
                      <Route path="payroll/reports"          element={<PayrollReportsPage />} />
                    </Route>

                    {/* ── monitoring — guarded ── */}
                    <Route element={<RequireModule module="monitoring" />}>
                      <Route path="monitoring"           element={<MonitoringPage />} />
                      <Route path="monitoring/settings"  element={<MonitoringSettingsPage />} />
                      <Route path="monitoring/analytics" element={<MonitoringAnalyticsPage />} />
                    </Route>

                    {/* ── projects — guarded ── */}
                    <Route element={<RequireModule module="projects" />}>
                      <Route path="projects"          element={<ProjectsPage />} />
                      <Route path="projects/reports"  element={<ReportsPage />} />
                      <Route path="projects/my-tasks" element={<MyTasksPage />} />
                      {/* Mirrors the route gate on POST /time-entries/{approve,reject,reopen}. */}
                      <Route path="projects/timesheets" element={<RequireUserPermission permission="time_entries.approve"><TimesheetApprovalsPage /></RequireUserPermission>} />
                      <Route path="projects/:id"      element={<ProjectDetailPage />} />
                    </Route>

                    {/* ── expenses — guarded ── */}
                    <Route element={<RequireModule module="expenses" />}>
                      <Route path="expenses"              element={<ExpensesPage />} />
                      <Route path="expenses/categories"   element={<ExpenseCategoriesPage />} />
                      <Route path="expenses/approvals"    element={<ExpensePendingApprovalsPage />} />
                      <Route path="expenses/reimburse"    element={<ExpenseReimbursePage />} />
                      <Route path="expenses/approval-chains" element={<ExpenseApprovalChainsPage />} />
                      {/* Every approval-policy route, reads included, is gated on
                          expenses.approve server side — mirror it here. */}
                      <Route path="expenses/approval-policies" element={<RequireUserPermission permission="expenses.approve"><ExpenseApprovalPoliciesPage /></RequireUserPermission>} />
                      <Route path="expenses/new"          element={<ExpenseEditorPage />} />
                      <Route path="expenses/:id"          element={<ExpenseDetailPage />} />
                      <Route path="expenses/:id/edit"     element={<ExpenseEditorPage />} />
                    </Route>

                    {/* ── esign — guarded ── */}
                    <Route element={<RequireModule module="esign" />}>
                      <Route path="esign" element={<RequireUserPermission permission="esign.view"><DocumentsListPage /></RequireUserPermission>} />
                      <Route path="esign/new" element={<RequireUserPermission permission="esign.view"><PrepareWizardPage /></RequireUserPermission>} />
                      <Route path="esign/templates" element={<RequireUserPermission permission="esign.view"><EsignTemplatesPage /></RequireUserPermission>} />
                      <Route path="esign/templates/:id" element={<RequireUserPermission permission="esign.view"><EsignTemplateEditorPage /></RequireUserPermission>} />
                      <Route path="esign/:id/prepare" element={<RequireUserPermission permission="esign.view"><PrepareWizardPage /></RequireUserPermission>} />
                      <Route path="esign/:id" element={<RequireUserPermission permission="esign.view"><DocumentDetailPage /></RequireUserPermission>} />
                    </Route>
                  </Route>
                )}

                {/* On app host, the root path lands users in the dashboard. */}
                {mode === 'app' && (
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                )}

                {/* Bare /agent/connect (e.g. a shared link, or the desktop
                    app's "open setup in browser" fallback) lands on the
                    same one-visit page under /dashboard. */}
                {showApp && (
                  <Route path="/agent/connect" element={<Navigate to="/dashboard/agent/connect" replace />} />
                )}

                {/* ──────────── MARKETING HOST (worqly.io) ──────────── */}
                {showMarketing && (
                  <Route element={<PublicOrRedirect />}>
                    <Route element={<MarketingLayout />}>
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/pricing" element={<PricingPage />} />
                      <Route path="/signup" element={<SignupPage />} />
                      <Route path="/customers" element={<CustomersPage />} />
                      <Route path="/customers/:slug" element={<CustomerDetailPage />} />
                      <Route path="/features" element={<FeaturesPage />} />
                      <Route path="/security" element={<SecurityPage />} />
                      <Route path="/integrations" element={<IntegrationsPage />} />
                      <Route path="/changelog" element={<ChangelogPage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/blog" element={<BlogIndexPage />} />
                      <Route path="/blog/:slug" element={<BlogPostPage />} />
                      <Route path="/docs" element={<DocsIndexPage />} />
                      <Route path="/docs/:slug" element={<DocsArticlePage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/verify" element={<VerifyPage />} />
                      <Route path="/verify/:number" element={<VerifyPage />} />
                      {/* Stripe Checkout return trip. checkout.service.ts sends
                          the payer here; without these routes every completed
                          online payment landed on the 404 page. */}
                      <Route path="/pay/success" element={<PaySuccessPage />} />
                      <Route path="/pay/cancel" element={<PayCancelPage />} />
                      <Route path="/legal/terms" element={<TermsPage />} />
                      <Route path="/legal/privacy" element={<PrivacyPage />} />
                      <Route path="/legal/dpa" element={<DpaPage />} />
                      <Route path="/legal/aup" element={<AupPage />} />
                    </Route>
                  </Route>
                )}

                {/* Status — accessible to everyone, on both hosts. */}
                <Route element={<MarketingLayout />}>
                  <Route path="/status" element={<StatusPage />} />
                </Route>

                {/* ──────────── CROSS-HOST REDIRECTS ──────────── */}
                {/* On marketing host, sending a user to /dashboard or /admin
                    bounces them to the app host (and vice-versa). */}
                {mode === 'marketing' && (
                  <>
                    <Route path="/dashboard/*" element={<ExternalRedirect to={APP_HOST} />} />
                    <Route path="/admin/*"     element={<ExternalRedirect to={APP_HOST} />} />
                  </>
                )}
                {mode === 'app' && (
                  <>
                    <Route path="/pricing"        element={<ExternalRedirect to={MARKETING_HOST} />} />
                    <Route path="/customers/*"    element={<ExternalRedirect to={MARKETING_HOST} />} />
                    <Route path="/features"       element={<ExternalRedirect to={MARKETING_HOST} />} />
                    <Route path="/security"       element={<ExternalRedirect to={MARKETING_HOST} />} />
                    <Route path="/integrations"   element={<ExternalRedirect to={MARKETING_HOST} />} />
                    <Route path="/changelog"      element={<ExternalRedirect to={MARKETING_HOST} />} />
                    <Route path="/about"          element={<ExternalRedirect to={MARKETING_HOST} />} />
                    <Route path="/signup"         element={<ExternalRedirect to={MARKETING_HOST} />} />
                    <Route path="/docs/*"         element={<ExternalRedirect to={MARKETING_HOST} />} />
                    <Route path="/contact"        element={<ExternalRedirect to={MARKETING_HOST} />} />
                    <Route path="/legal/*"        element={<ExternalRedirect to={MARKETING_HOST} />} />
                  </>
                )}

                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              </ConfirmProvider>
            </AuthProvider>
          </BrandingProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

/**
 * Wraps the public marketing routes. Anyone — authenticated or not — sees
 * the marketing pages via <Outlet/>. The MarketingNav has a Sign-in button
 * that takes authenticated users to their workspace; we DO NOT silently
 * auto-redirect here, because if app.worqly.io ever becomes unreachable
 * (DNS propagation, regional ISP issues), an automatic bounce would trap
 * the user in an unreachable loop with no way to get back to marketing.
 */
function PublicOrRedirect() {
  // Intentional: no auto-redirect. See doc comment above.
  return <Outlet />;
}

/**
 * Hard redirect to the same pathname/search on a different host. Used for
 * wrong-host requests (e.g. someone hits worqly.io/dashboard → bounce to
 * app.worqly.io/dashboard).
 */
function ExternalRedirect({ to }: { to: string }) {
  React.useEffect(() => {
    const { pathname, search, hash } = window.location;
    window.location.replace(`https://${to}${pathname}${search}${hash}`);
  }, [to]);
  return null;
}

export default App;
